import {
  AnchorButton,
  Button,
  HTMLTable,
  PopoverInteractionKind,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { Tooltip } from 'bones/components/atoms/Tooltip';
import { GenericPropertiesRow } from 'bones/components/molecules/GenericPropertiesEditor/GenericPropertiesRow';
import {
  GenericPropertiesValidator,
  GenericProperty,
  GenericPropertySuggestion,
} from 'bones/components/molecules/GenericPropertiesEditor/types';

export const NEW_GENERIC_PROPERTY_KEY = '__NEW_PROPERTY__';

export interface GenericPropertiesEditorProps {
  disabled?: boolean;
  editorTestId?: string;
  nameHeader?: string;
  maxSuggestions?: number;
  onAddProperty: (property: GenericProperty) => void;
  onPropertyChange: (property: GenericProperty) => void;
  onRemoveProperty: (property: GenericProperty) => void;
  preventAddingNewProperty?: (property: GenericProperty) => boolean;
  properties: GenericProperty[];
  propertyActionTestId?: string;
  propertyNameTestId?: string;
  propertySuggestions?: GenericPropertySuggestion[];
  propertyValueTestId?: string;
  rowTestId?: (propertyKey: string, rowIndex?: number) => string;
  suggestionItemTestId?: (propertyName: string, rowIndex?: number) => string;
  usePortal?: boolean;
  validator?: GenericPropertiesValidator;
  valueHeader?: string;
}

export const GenericPropertiesEditor: React.VFC<
  GenericPropertiesEditorProps
> = ({
  disabled,
  editorTestId,
  nameHeader = 'Property',
  maxSuggestions,
  onAddProperty,
  onPropertyChange,
  onRemoveProperty,
  preventAddingNewProperty,
  properties,
  propertyActionTestId,
  propertyNameTestId,
  propertySuggestions,
  propertyValueTestId,
  rowTestId = () => '',
  suggestionItemTestId,
  usePortal,
  validator,
  valueHeader = 'Value',
}) => {
  const [newPropertyName, setNewPropertyName] = React.useState<string>('');
  const [newPropertyValue, setNewPropertyValue] = React.useState<string>('');

  const templateRow = (
    <GenericPropertiesRow
      key={NEW_GENERIC_PROPERTY_KEY}
      actionElementRenderer={(disabled, tooltipContent, intent) => (
        <Tooltip
          content={tooltipContent}
          disabled={!tooltipContent || !disabled}
          interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
          position={Position.TOP}
        >
          <AnchorButton
            data-testid={propertyActionTestId}
            disabled={disabled}
            intent={intent}
            onClick={() => {
              const newProperty: GenericProperty = {
                key: uuid(),
                name: newPropertyName,
                value: newPropertyValue,
              };

              if (preventAddingNewProperty?.(newProperty)) {
                return;
              }

              setNewPropertyName('');
              setNewPropertyValue('');
              onAddProperty(newProperty);
            }}
            text="Add"
          />
        </Tooltip>
      )}
      disabled={disabled}
      isNewRow
      maxSuggestions={maxSuggestions}
      name={newPropertyName}
      properties={properties}
      propertyNameTestId={propertyNameTestId}
      propertySuggestions={propertySuggestions}
      propertyValueTestId={propertyValueTestId}
      suggestionItemTestId={suggestionItemTestId}
      testId={rowTestId(NEW_GENERIC_PROPERTY_KEY, 0)}
      updateProperty={(name, value) => {
        setNewPropertyName(name);
        setNewPropertyValue(value);
      }}
      usePortal={usePortal}
      validator={validator}
      value={newPropertyValue}
    />
  );

  const currentRows = properties.map((property, index) => (
    <GenericPropertiesRow
      key={property.key}
      actionElementRenderer={(disabled) => (
        <Button
          data-testid={propertyActionTestId}
          disabled={disabled}
          icon={IconNames.CROSS}
          minimal
          onClick={() => onRemoveProperty(property)}
        />
      )}
      disabled={disabled}
      isNewRow={false}
      maxSuggestions={maxSuggestions}
      name={property.name}
      properties={properties}
      propertyNameTestId={propertyNameTestId}
      propertySuggestions={propertySuggestions}
      propertyValueTestId={propertyValueTestId}
      suggestionItemTestId={suggestionItemTestId}
      testId={rowTestId(property.key, index + 1)}
      updateProperty={(name: string, value: string) =>
        onPropertyChange({ key: property.key, name, value })
      }
      usePortal={usePortal}
      validator={validator}
      value={property.value}
    />
  ));

  return (
    <HTMLTable
      className="sd-generic-properties-editor"
      data-testid={editorTestId}
    >
      <thead>
        <tr className="sd-generic-properties-editor__row">
          <th
            aria-hidden="true"
            className="sd-generic-properties-editor__hidden-tooltip-cell"
          />
          <th>{nameHeader}</th>
          <th>{valueHeader}</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {templateRow}
        {currentRows}
      </tbody>
    </HTMLTable>
  );
};
